// Element counting rules for live boards and parent limits (PL3.c).
// Must stay byte-for-byte identical with functions-common/src/config/elementCounting.ts.

export type CountedBoard = 'VISIONBOARD' | 'DASHBOARD'
export type CountedElementType = 'GOAL' | 'TASK' | 'SUBTASK'
export type BoardCounterKey = 'goals' | 'tasks' | 'subtasks' | 'checklists'
export type ParentCounterKey = 'tasks' | 'subtasks' | 'checklists'

export const COUNTED_BOARDS: readonly CountedBoard[] = ['VISIONBOARD', 'DASHBOARD'] as const
export const COUNTED_ELEMENT_TYPES: readonly CountedElementType[] = ['GOAL', 'TASK', 'SUBTASK'] as const

export function isCountedBoard(board: unknown): board is CountedBoard {
  return board === 'VISIONBOARD' || board === 'DASHBOARD'
}

export function isCountedElementType(type: unknown): type is CountedElementType {
  return type === 'GOAL' || type === 'TASK' || type === 'SUBTASK'
}

export function isCountedElement(element: unknown): boolean {
  if (!element || typeof element !== 'object') {
    return false
  }
  const el = element as Record<string, unknown>
  if (!isCountedElementType(el.type)) {
    return false
  }
  if (!isCountedBoard(el.board)) {
    return false
  }
  if (el.sample) {
    return false
  }
  const edu = el.edu
  if (edu && typeof edu === 'object' && (edu as Record<string, unknown>).archivedYear === true) {
    return false
  }
  return true
}

export function isCountedChecklist(checklist: unknown): boolean {
  if (!checklist || typeof checklist !== 'object') {
    return false
  }
  const item = checklist as Record<string, unknown>
  if (isCountedElementType(item.type)) {
    return false
  }
  const kind = item.nodeKind
  if (kind !== undefined && kind !== null && kind !== 'CHECKLIST') {
    return false
  }
  if (item.isArchived === true) {
    return false
  }
  if (!isCountedBoard(item.board)) {
    return false
  }
  if (item.sample) {
    return false
  }
  if (item.parentContainerId === 'workspace-default-checklist') {
    return false
  }
  return true
}

export function boardCounterKeyForType(type: unknown): BoardCounterKey | null {
  if (type === 'GOAL') return 'goals'
  if (type === 'TASK') return 'tasks'
  if (type === 'SUBTASK') return 'subtasks'
  return null
}

export function parentCounterKeyForType(type: unknown): ParentCounterKey | null {
  if (type === 'TASK') return 'tasks'
  if (type === 'SUBTASK') return 'subtasks'
  return null
}

export function boardLimitKeyForType(
  type: unknown,
): 'maxGoalsPerBoard' | 'maxTasksPerBoard' | 'maxSubtasksPerBoard' | null {
  if (type === 'GOAL') return 'maxGoalsPerBoard'
  if (type === 'TASK') return 'maxTasksPerBoard'
  if (type === 'SUBTASK') return 'maxSubtasksPerBoard'
  return null
}

export function parentLimitKeyForType(
  type: unknown,
): 'maxTasksPerGoal' | 'maxSubtasksPerTask' | null {
  if (type === 'TASK') return 'maxTasksPerGoal'
  if (type === 'SUBTASK') return 'maxSubtasksPerTask'
  return null
}

export const CAP_KEY_TO_FEATURE_KEY = {
  maxGoalsPerBoard: 'maxGoalsPerBoard',
  maxTasksPerBoard: 'maxTasksPerBoard',
  maxSubtasksPerBoard: 'maxSubtasksPerBoard',
  maxChecklistsPerBoard: 'maxChecklistsPerBoard',
  maxTasksPerGoal: 'maxTasksPerGoal',
  maxSubtasksPerTask: 'maxSubtasksPerTask',
  maxChecklistsPerContainer: 'maxChecklistsPerContainer',
} as const

export function isSyntheticParentId(parentId: string | null | undefined): boolean {
  if (!parentId) return true
  return parentId === 'root' || parentId === 'workspace-default-checklist' || parentId.startsWith('board-root:')
}

export function resolveBinDeletedFromBoard(
  currentBoard?: string | null,
  existingDeletedFrom?: string | null,
  activeBoard?: string | null,
): string {
  const effectiveCurrent = currentBoard ?? activeBoard ?? 'DASHBOARD'
  if (effectiveCurrent !== 'BIN') return effectiveCurrent
  return (existingDeletedFrom && existingDeletedFrom !== 'BIN') ? existingDeletedFrom : 'DASHBOARD'
}

